import { Location } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  HostListener,
  ViewChild,
  ElementRef,
  inject,
  computed,
  Signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Language, SetActiveLanguage } from '@teamfoster/sdk/language-ngrx';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { getRouterState } from 'src/app/store';
import { state } from '@angular/animations';
import { SlugsService } from 'src/app/shop/services';
import { getSelectedCollection, getSelectedproduct } from 'src/app/shop';
import { getActiveStorefront } from 'src/app/storefronts';

@Component({
  selector: 'app-lang-switcher',
  templateUrl: './lang-switcher.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class LangSwitcherComponent {
  private store = inject(Store);
  private slugs = inject(SlugsService);

  @ViewChild('dropdownpanel') dropdownpanel?: ElementRef;
  @Input() languages: Language[] = [];
  @Input() activeLang!: string;
  dropdownOpen = false;

  private _activeStorefront$ = this.store.selectSignal(getActiveStorefront);
  private _selectedProduct = this.store.selectSignal(getSelectedproduct);
  private _selectedCollection = this.store.selectSignal(getSelectedCollection);

  private _domain = computed(() => (this._activeStorefront$()?.id === 2 ? 'outlet.kadodesign' : 'kadodesign'));
  private _domainMap: Signal<{ [key: string]: string }> = computed(() => {
    return {
      'nl-NL': `https://${this._domain()}.nl`,
      'en-GB': `https://${this._domain()}.com`,
      'de-DE': `https://${this._domain()}.de`,
    };
  });

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: Event): void {
    if (!this.dropdownpanel?.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }

  async setLanguage(lang: Language) {
    if (document.location.hostname === 'localhost') {
      this.store.dispatch(SetActiveLanguage({ activeLang: lang.code }));
    }

    const langCode = lang.code?.split('-')[0]?.toUpperCase();

    // Get new domain
    const newDomain = this._domainMap()[lang.code];
    let collectionSlug = '';
    let productSlug = '';
    // get translated slugs
    if (this._selectedCollection()) {
      // get new collection Id
      collectionSlug = (await lastValueFrom(this.slugs.getCollectionSlugById(this._selectedCollection()!.id, langCode))).handle;
    }
    if (this._selectedProduct()) {
      productSlug = (await lastValueFrom(this.slugs.getProductSlugById(this._selectedProduct().id, langCode))).handle;
    }

    let finalUrl = `${newDomain}${document.location.pathname}${document.location.search}`;

    if (collectionSlug || productSlug) {
      const prefix = collectionSlug ? 'collections' : 'products';
      const slugs = [collectionSlug, productSlug].filter(a => a);
      finalUrl = `${newDomain}/${prefix}/${slugs.join('/')}`;
    }

    // redirect
    document.location.href = finalUrl;
  }

  getFlag(code: string) {
    return `/assets/img/country-flags/${code.split('-')?.[1]?.toLowerCase()}.svg`;
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  get activeLanguage(): Language {
    return this.languages.find(a => a.code === this.activeLang) ?? this.languages[0];
  }
}
